"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCountGroupTimeData = void 0;
const react_hooks_1 = require("@apollo/react-hooks");
const MetaModelRegistry_1 = __importDefault(require("@opsbase/lib-data-metamodel/MetaModelRegistry"));
const ChartingPalette_1 = __importDefault(require("@opsbase/lib-palette/ChartingPalette"));
const TimeInterval_1 = __importDefault(require("@opsbase/lib-time/TimeInterval"));
const useSelf_1 = __importDefault(require("@opsbase/react-ctrl-self/useSelf/useSelf"));
const useDataTree_1 = __importDefault(require("@opsbase/web-ctrl-datatree/useDataTree"));
const useView_1 = __importDefault(require("@opsbase/web-ctrl-view/useView"));
const graphql_tag_1 = __importDefault(require("graphql-tag"));
const uniq_1 = __importDefault(require("lodash/uniq"));
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const react_1 = require("react");
const calcIntervalForFilter_1 = require("../helpers/calcIntervalForFilter");
function useCountGroupTimeData(timeDimension, otherDimension) {
    var _a;
    const dt = (0, useDataTree_1.default)();
    const view = (0, useView_1.default)(dt.viewId);
    const [changed, setChanged] = (0, react_1.useState)();
    const loading = (0, react_1.useRef)(true);
    const data = (0, react_1.useRef)([]);
    const self = (0, useSelf_1.default)();
    const state = (0, react_1.useRef)({
        date: undefined,
        self: undefined,
        timeDimension,
        otherDimension,
    });
    const intervalFilter = (_a = view === null || view === void 0 ? void 0 : view.filters) === null || _a === void 0 ? void 0 : _a.find(f => f.queryFieldKey === timeDimension);
    const interval = (0, react_1.useMemo)(() => (0, calcIntervalForFilter_1.calcIntervalForFilter)(intervalFilter), [intervalFilter]);
    const skip = !timeDimension ||
        !otherDimension ||
        (state.current.date >= view.groupsChanged &&
            state.current.date >= view.filtersChanged &&
            state.current.self === self &&
            state.current.timeDimension === timeDimension &&
            state.current.otherDimension === otherDimension);
    const gqlQueryName = MetaModelRegistry_1.default[view.type].aggQueryName;
    const gqlStr = (0, graphql_tag_1.default) `
		query ${gqlQueryName}($view: LMSS!, $dim1: String!, $dim2: String) {
			${gqlQueryName}(view: $view, dim1: $dim1, dim2: $dim2, groupsOnly: true)
		}
	`;
    const { loading: gqlLoading, data: gqlData } = (0, react_hooks_1.useQuery)(gqlStr, {
        variables: { view, dim1: timeDimension, dim2: otherDimension },
        skip,
    });
    // --------------------
    // This is the primary part of the hook
    // It parses the rawData into data that can be used by the charts
    (0, react_1.useEffect)(() => {
        if (gqlLoading && !loading.current) {
            loading.current = true;
            return setChanged(new Date());
        }
        if (gqlLoading)
            return;
        if (skip)
            return;
        loading.current = false;
        state.current.date = new Date();
        state.current.timeDimension = timeDimension;
        state.current.otherDimension = otherDimension;
        // Get Our Raw Data
        const rawData = (gqlData === null || gqlData === void 0 ? void 0 : gqlData[gqlQueryName]) || [];
        // Get the Time Intervals
        const timeIntervals = (0, uniq_1.default)(rawData.filter(a => { var _a; return !!((_a = a === null || a === void 0 ? void 0 : a.dim1) === null || _a === void 0 ? void 0 : _a.id); }).map(a => { var _a; return parseInt((_a = a === null || a === void 0 ? void 0 : a.dim1) === null || _a === void 0 ? void 0 : _a.id); })).sort((a, b) => a - b);
        const groupBy = view.groupBys.find(gb => gb.queryFieldKey === otherDimension);
        const groups = dt.groupsForGroupBy.get(groupBy);
        const autoPalette = ChartingPalette_1.default.generatePalette((groups === null || groups === void 0 ? void 0 : groups.length) || 0);
        // Convert our Raw Data into Chart Data
        data.current = groups
            // Remove empty groups
            .filter(group => !!rawData.find(agg => { var _a; return ((_a = agg.dim2) === null || _a === void 0 ? void 0 : _a.id) === group.id; }))
            // Create a bar chart for each time interval
            .map((group, groupIdx) => timeIntervals.map((timeDim, timeDimIdx) => {
            var _a;
            const value = ((_a = rawData.find(agg => { var _a; return agg.dim1.id === timeDim.toString() && ((_a = agg.dim2) === null || _a === void 0 ? void 0 : _a.id) === group.id; })) === null || _a === void 0 ? void 0 : _a.value) || 0;
            return aggToChartDatum(timeDim, timeDimIdx, value, interval, group, group.color || autoPalette[groupIdx]);
        }));
        // Update Ctx
        setChanged(new Date());
    }, [
        dt.groupsForGroupBy,
        gqlData,
        gqlLoading,
        gqlQueryName,
        interval,
        otherDimension,
        skip,
        self,
        timeDimension,
        view.groupBys,
    ]);
    // --------------------
    return (0, react_1.useMemo)(() => ({
        loading: loading.current,
        data: data.current,
    }), 
    // eslint-disable-next-line
    [changed, self]);
}
exports.useCountGroupTimeData = useCountGroupTimeData;
exports.default = useCountGroupTimeData;
function aggToChartDatum(timeKey, timeIdx, value, interval, group, color) {
    const from = (0, moment_timezone_1.default)(timeKey);
    let timeLabel = from.format('DD MMM');
    if (interval !== TimeInterval_1.default.DAY) {
        const to = from.clone().add(1, interval).subtract(1, TimeInterval_1.default.DAY);
        timeLabel = timeLabel + ' - ' + to.format('DD MMM');
    }
    const timeCount = value;
    return {
        // X is time
        x: timeIdx + 1,
        xDim: { id: timeKey, label: timeLabel },
        // Y is value per group
        y: timeCount,
        yRaw: timeCount,
        yDim: { id: group.id, label: group.title },
        color1: color.main,
        color2: color.dark,
        label: timeCount + ' ' + group.title + ' on ' + timeLabel,
    };
}
